import axios from 'axios';
import { getCookies } from '../store/useCookies';
import { message } from 'antd';
import { jwtDecode } from 'jwt-decode';

export const CONNECT_API = process.env.REACT_APP_DOMAIN;
export const CONNECT_API_UPLOAD_IMAGE = process.env.REACT_APP_UPLAOD;


const isValidCompanyId = (id) => {
  if (id == null || id === '' || id === 'undefined' || id === 'null' || id === 0 || id === '0') {
    return false;
  }
  const n = parseInt(String(id), 10);
  return Number.isFinite(n) && n > 0;
};

const readSessionCompanyId = () => {
  const id = sessionStorage.getItem('activeCompanyId');
  return isValidCompanyId(id) ? String(id) : '';
};

const api = axios.create({
  baseURL: CONNECT_API,
  //   withCredentials: true,

  headers: {
    'Content-Type': 'application/json',
    'Authorization': sessionStorage.getItem('token') || '',
    'company_id': readSessionCompanyId(),
    'user_id': sessionStorage.getItem('id') || '',
  },
  timeout: 10000, // 10 seconds
});

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  const companyId = readSessionCompanyId();
  const userId = sessionStorage.getItem('id');

  if (token) {
    config.headers.Authorization = token;
  }
  if (companyId) {
    config.headers.company_id = companyId;
  } else {
    delete config.headers.company_id;
  }
  if (userId) {
    config.headers.user_id = userId;
  }
  return config;
});

// ฟังก์ชันสำหรับอัปเดต company_id ใน header
export const updateCompanyIdHeader = (companyId) => {
  const value = isValidCompanyId(companyId) ? String(companyId) : '';
  api.defaults.headers['company_id'] = value;
};

const apiupload = axios.create({
  baseURL: CONNECT_API_UPLOAD_IMAGE,
  //   withCredentials: true,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

/**
 * จัดการ "token หมดอายุ" ให้ทั้งระบบที่เดียว
 *
 * ปัญหาเดิม: ไม่มีใครต่ออายุ token เลย (ตัวเรียก refresh ใน App.js ถูก comment ไว้)
 * และไม่มีที่ไหนดัก 401 ด้วย พอ token หมดผู้ใช้จะค้างอยู่หน้าเดิม กดอะไรก็เด้ง
 * error แดงไปเรื่อย ๆ โดยไม่มีอะไรบอกให้ login ใหม่ — ที่ผ่านมาไม่มีใครสังเกต
 * เพราะ endpoint ส่วนใหญ่ไม่ได้ตรวจ token จึงยังตอบ 200 ทั้งที่ token หมดแล้ว
 *
 * ทำงาน: เจอ 401 → ขอ token ใหม่ด้วย refreshToken → ยิงคำขอเดิมซ้ำ
 *        ถ้าต่ออายุไม่ได้ (refreshToken หมดด้วย) → ล้าง session แล้วกลับหน้า login
 */
let refreshPromise = null;   // รวม refresh ให้เหลือครั้งเดียว แม้หลายคำขอจะ 401 พร้อมกัน
let loggingOut = false;      // กันเด้ง message/redirect ซ้ำหลายรอบ

const forceRelogin = () => {
  if (loggingOut) return;
  loggingOut = true;
  message.destroy();
  message.error('หมดเวลาใช้งาน กรุณาเข้าสู่ระบบใหม่');
  setTimeout(() => {
    sessionStorage.clear();
    window.location.href = '/';
  }, 1500);
};

const tryRefreshToken = () => {
  if (refreshPromise) return refreshPromise;
  const tokenRefresh = sessionStorage.getItem('refreshToken');
  if (!tokenRefresh) return Promise.resolve(null);

  refreshPromise = api
    .post('/api/v1/authen/refresh-token', { tokenRefresh })
    .then((res) => {
      const result = res?.data?.result;
      if (!result?.accessToken) return null;
      sessionStorage.setItem('token', result.accessToken);
      if (result.refreshToken) sessionStorage.setItem('refreshToken', result.refreshToken);
      return result.accessToken;
    })
    .catch(() => null)
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
};

/**
 * คืน token ใหม่ถ้าต่ออายุสำเร็จ / คืน null ถ้าไม่ใช่เคส 401 หรือกู้ไม่ได้
 * เส้น /authen/ ไม่เข้าเงื่อนไข — ไม่งั้น refresh ที่ล้มเหลวจะวนเรียกตัวเอง
 */
const handleUnauthorized = async (err, path, retried) => {
  if (retried) return null;
  if (err?.response?.status !== 401) return null;
  if (String(path).includes('/api/v1/authen/')) return null;

  const fresh = await tryRefreshToken();
  if (!fresh) {
    forceRelogin();
    return null;
  }
  return fresh;
};

/**
 * ต่ออายุ token "ก่อน" มันหมด — เรียกจากตัวจับเวลาใน App.js
 *
 * ทำไมต้องมี: เดิมการต่ออายุเกิดเฉพาะตอนโดน 401 เท่านั้น = ใช้ error path
 * เป็นกลไกปกติ ซึ่งเปราะ (ถ้า refresh พลาดสักครั้ง = เด้งออกกลางงาน)
 * มีตัวนี้แล้ว token จะไม่มีวันหมดขณะแท็บเปิดอยู่ 401 จะเหลือแค่กรณี
 * refresh token หมดจริง (7 วัน) ซึ่งเป็นสิ่งที่เราตั้งใจให้เด้งออก
 *
 * ⚠️ ห้าม forceRelogin ที่นี่ — เน็ตกระตุกชั่วคราวก็ทำให้ refresh ล้มเหลวได้
 *    ปล่อยให้ทางเดิม (401 → tryRefreshToken → forceRelogin) เป็นคนตัดสิน
 *    เพราะตอนนั้นเรารู้แน่ว่า server ปฏิเสธจริง ไม่ใช่เน็ตมีปัญหา
 */
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // เหลือน้อยกว่า 5 นาที = ต่ออายุ

export const refreshTokenIfExpiringSoon = async () => {
  const token = sessionStorage.getItem('token');
  if (!token || !sessionStorage.getItem('refreshToken')) return false;

  let expMs;
  try {
    const { exp } = jwtDecode(token);
    if (!exp) return false;
    expMs = exp * 1000;
  } catch (e) {
    return false; // token พัง — ปล่อยให้ 401 จัดการ
  }

  if (expMs - Date.now() > REFRESH_MARGIN_MS) return false;

  const fresh = await tryRefreshToken();
  if (!fresh) console.warn('[auth] ต่ออายุ token ล่วงหน้าไม่สำเร็จ จะลองใหม่รอบหน้า');
  return Boolean(fresh);
};

const checkHeaders = (path) => {
  // ทุกเส้นใต้ /authen/ ไม่ต้องมี company_id — login, refresh-token,
  // forgot-password (ยิงจากหน้า login ตอนยังไม่มี session เลย) และ change-password
  const isAuthPath = path.includes('/api/v1/authen/');
  const companyId = readSessionCompanyId();
  const isBackoffice = window.location.pathname.includes('/backoffice');
  if (!companyId && !isAuthPath && !isBackoffice) {
    if (window.location.pathname !== '/') {
      message.destroy();
      message.error('ไม่พบข้อมูลบริษัท กรุณา login ใหม่', {
        duration: 5,
      });
      setTimeout(() => {
        sessionStorage.clear();
        window.location.href = '/';
      }, 4000);
    }
    return false;
  }
  return true;
};

/**
 * ฟังก์ชั่นสำหรับ ยิงข้อมูล
 * @param {string} path
 * @param {{}} obj
 * @return {{}}
 */
export const POST = (path, obj, token = sessionStorage.getItem('token'), _retry = 0) =>
  new Promise((resolve, reject) => {
    if (!checkHeaders(path)) {
      return reject({ success: false, message: 'Missing Company ID' });
    }
    api
      .post(path, obj, {
        headers: {
          Authorization: token,
        },
        timeout: 120000,
      })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        } else {
          response.data
            ? reject(response.data)
            : reject({ success: false, message: response.statusText });
        }
      })
      .catch((err) => {
        // Handle 429: retry แบบมีลิมิต + backoff (กัน retry storm ที่เลี้ยงตัวเอง)
        if (err.response && err.response.status === 429 && _retry < 2) {
          const delay = 3000 * Math.pow(2, _retry); // 3s, 6s
          console.warn(`Rate limited, retry ${_retry + 1}/2 in ${delay}ms`);
          setTimeout(() => {
            POST(path, obj, token, _retry + 1).then(resolve).catch(reject);
          }, delay);
        } else {
          // token หมด → ต่ออายุแล้วยิงซ้ำให้อัตโนมัติ ผู้ใช้ไม่ต้องทำอะไร
          handleUnauthorized(err, path, _retry > 0).then((fresh) => {
            if (fresh) POST(path, obj, fresh, 1).then(resolve).catch(reject);
            else reject(err);
          });
        }
      });
  });

/**
 * ฟังก์ชั่นสำหรับ ดึงข้อมูล
 * @param {string} path
 *
 */
export const GET = (path, token = sessionStorage.getItem('token'), _retry = 0) =>
  new Promise((resolve, reject) => {
    if (!checkHeaders(path)) {
      return reject({ success: false, message: 'Missing Company ID' });
    }
    api
      .get(path, {
        headers: {
          Authorization: token,
        },
        timeout: 120000,
      })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        } else {
          response.data
            ? reject(response.data)
            : reject({ success: false, message: response.statusText });
        }
      })
      .catch((err) => {
        // Handle 429: retry แบบมีลิมิต + backoff (กัน retry storm ที่เลี้ยงตัวเอง)
        if (err.response && err.response.status === 429 && _retry < 2) {
          const delay = 3000 * Math.pow(2, _retry); // 3s, 6s
          console.warn(`Rate limited, retry ${_retry + 1}/2 in ${delay}ms`);
          setTimeout(() => {
            GET(path, token, _retry + 1).then(resolve).catch(reject);
          }, delay);
        } else {
          // token หมด → ต่ออายุแล้วยิงซ้ำให้อัตโนมัติ ผู้ใช้ไม่ต้องทำอะไร
          handleUnauthorized(err, path, _retry > 0).then((fresh) => {
            if (fresh) GET(path, fresh, 1).then(resolve).catch(reject);
            else reject(err);
          });
        }
      });
  });

export const DELETE = (path, token = sessionStorage.getItem('token')) =>
  new Promise((resolve, reject) => {
    if (!checkHeaders(path)) {
      return reject({ success: false, message: 'Missing Company ID' });
    }
    api
      .delete(path, {
        headers: {
          Authorization: token,
        },
        timeout: 120000,
      })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        } else {
          response.data
            ? reject(response.data)
            : reject({ success: false, message: response.statusText });
        }
      })
      .catch((err) => {
        handleUnauthorized(err, path, false).then((fresh) => {
          if (fresh) DELETE(path, fresh).then(resolve).catch(reject);
          else reject(err);
        });
      });
  });

/**
* ฟังก์ชั่นสำหรับ อัปเดตข้อมูล
* @param {string} path
* @param {{}} obj
* @return {{}}
*/
export const PUT = (path, obj, token = sessionStorage.getItem('token')) =>
  new Promise((resolve, reject) => {
    if (!checkHeaders(path)) {
      return reject({ success: false, message: 'Missing Company ID' });
    }
    api
      .put(path, obj, {
        headers: {
          Authorization: token,
        },
        timeout: 120000,
      })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        } else {
          response.data
            ? reject(response.data)
            : reject({ success: false, message: response.statusText });
        }
      })
      .catch((err) => {
        handleUnauthorized(err, path, false).then((fresh) => {
          if (fresh) PUT(path, obj, fresh).then(resolve).catch(reject);
          else reject(err);
        });
      });
  });


/**
 * ฟังก์ชั่นสำหรับ อัพโหลดไฟล์
 * @param {string} path
 * @param {FormData} formdata
 * @return {{}}
 */
export const UPLOAD = (path, formdata, token = sessionStorage.getItem('token')) =>
  new Promise((resolve, reject) => {
    apiupload
      .post(path, formdata, {
        // headers: {
        //   Authorization: token,
        // },
      })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          resolve(response.data);
        } else {
          response.data
            ? reject(response.data)
            : reject({ success: false, message: response.statusText });
        }
      })
      .catch((err) => reject(err));
  });

// import { create } from 'apisauce';
// import { getCookies } from '../store/useCookies';
// export const ip = 'http://192.168.1.169:2000';

// export const CONNECT_API = 'http://api.one2tickets.com/api'
// const api = create({
//   baseURL: CONNECT_API,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'application/json',
//   },
//   timeout: 10000, // 10 seconds
// });

// const apiupload = create({
//   baseURL: CONNECT_API,
//   withCredentials: true,
//   headers: {
//     'Content-Type': 'multipart/form-data',
//   },
// });

// /**
//  * ฟังก์ชั่นสำหรับ ยิงข้อมูล
//  * @param {string} path
//  * @param {{}} obj
//  * @return {{}}
//  */
// export const POST = (path, obj, token = getCookies('accessToken')) =>
//   new Promise((resolve, reject) => {
//     api
//       .post(path, obj, {
//         headers: {
//           Authorization: token,
//         },
//       })
//       .then((response) => {
//         if (response.ok) {
//           resolve(response.data);
//         } else {
//           response.data
//             ? reject(response.data)
//             : reject({ success: false, message: response.problem });
//         }
//       })
//       .catch((err) => reject(err));
//   });

// /**
//  * ฟังก์ชั่นสำหรับ ดึงข้อมูล
//  * @param {string} path
//  *
//  */
// export const GET = (path, token = getCookies('accessToken')) =>
//   new Promise((resolve, reject) => {
//     api
//       .get(
//         path,
//         {},
//         {
//           headers: {
//             Authorization: token,
//           },
//         },
//       )
//       .then((response) => {
//         if (response.ok) {
//           resolve(response.data);
//         } else {
//           response.data
//             ? reject(response.data)
//             : reject({ success: false, message: response.problem });
//         }
//       })
//       .catch((err) => reject(err));
//   });

// export const UPLOAD = (path, formdata, token = getCookies('accessToken')) =>
//   new Promise((resolve, reject) => {
//     apiupload
//       .post(path, formdata, {
//         headers: {
//           Authorization: token,
//         },
//       })
//       .then((response) => {
//         if (response.ok) {
//           resolve(response.data);
//         } else {
//           response.data
//             ? reject(response.data)
//             : reject({ success: false, message: response.problem });
//         }
//       })
//       .catch((err) => reject(err));
//   });
